/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { appConstants } from '@app/common/app-constants';
import { Logger } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { FhirCastResource } from '@app/di/channel/fhircast/model/fhircast-resource';
import { DiConfigService } from '@app/di/di-config.service';
import { WebsocketHandlerService } from '../websocket/websocket-handler.service';
import { ApplicationConfig, ConnectionConfig } from '../websocket/websocket-registry';
import { UtilService } from '@app/util/util.service';

const log = new Logger('FhircastService');

@Injectable({
  providedIn: 'root'
})
export class FhircastService {
  constructor(
    private dataService: ProxyDataService,
    private diConfigService: DiConfigService,
    private notificationService: WebsocketHandlerService
  ) {}

  async register(topic: string, appConfig: ApplicationConfig) {
    try {
      const url = await this.subscribe(topic, appConfig.id, appConfig.hubEvents);
      log.info(`Hub ← WFM Subscribe: appId=${appConfig.id} topic=${topic} events=${appConfig.hubEvents?.join(', ') ?? 'default'}`);
      const connectionConfig: ConnectionConfig = { ...appConfig, url };
      await this.notificationService.registerConnection(connectionConfig);
      log.info(`Hub ↔ WFM WebSocket registered: appId=${appConfig.id} url=${url}`);
    } catch (error) {
      throw error;
    }
  }

  sendNotification(id: string, message: string) {
    if (this.notificationService.hasConnection(id)) {
      log.info(`${id}: Message Sent: ${message}`);
      this.notificationService.getConnection(id).sendMessage(message);
      return;
    }
    log.error(`Cannot send message to ${id}`, 'Application not registered');
  }

  unregister(appId: string) {
    return this.notificationService.unregister(appId);
  }

  private async subscribe(topic: string, appId: string, hubEvents?: string[]) {
    const { subscribeUrl, urlParams } = this.getSubscriptionParameters(topic, appId, hubEvents);
    const { RetryMaxcount, RetryPeriod } = this.diConfigService.integrationConfig.Fhircast;
    const httpOptions = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: ''
      }
    };
    try {
      const data = await UtilService.retryRequest(
        async () => {
          const token = await this.diConfigService.getJwtToken();
          httpOptions.headers.Authorization = `Bearer ${token}`;
          return this.dataService.httpPost(subscribeUrl, urlParams, httpOptions);
        },
        RetryMaxcount,
        RetryPeriod,
        `${appId} : Subscription : Fhircast : Failed`
      );
      return Object.values(data)[0] as string;
    } catch (error) {
      log.error(`Fhircast: Application: ${appId}: Subscribe failed for topic ${topic}`, error);
      throw error;
    }
  }

  async publish(payload: FhirCastResource, appId?: string) {
    const { Url, PublishEndpoint } = this.diConfigService.integrationConfig.Fhircast;
    const publishUrl = Url + PublishEndpoint.v1;
    const httpOptions = {
      headers: {
        Authorization: ''
      }
    };
    log.info(`Hub ← ${appId ?? 'WFM'} Publish: event=${payload?.event?.['hub.event'] ?? 'unknown'} id=${payload?.id ?? 'unknown'}`);
    try {
      const data = await UtilService.retryRequest(
        async () => {
          const token = await this.diConfigService.getJwtToken();
          httpOptions.headers.Authorization = `Bearer ${token}`;
          return this.dataService.httpPost(publishUrl, payload, httpOptions);
        },
        3,
        1000,
        `${appId ?? ''} : Publish : Fhircast : Failed`
      );
      log.info(`Hub → ${appId ?? 'WFM'} Publish OK: event=${payload?.event?.['hub.event'] ?? 'unknown'} id=${payload?.id ?? 'unknown'}`);
      return data;
    } catch (error) {
      log.error(`Hub ← ${appId ?? 'WFM'} Publish failed: event=${payload?.event?.['hub.event'] ?? 'unknown'} id=${payload?.id ?? 'unknown'}`, error);
      throw error;
    }
  }

  private getSubscriptionParameters(topic: string, appId: string, hubEvents?: string[]) {
    const { Url, SubscribeEndpoint } = this.diConfigService.integrationConfig.Fhircast;
    const subscribeUrl = Url + SubscribeEndpoint.v1;
    const subscriptionEvents = hubEvents?.join(', ') || 'imagingstudy-close, syncsuccess, syncerror';

    // Use the caller's appId as the FHIRcast applicationId so that DA and EV
    // produce distinct subscriptions on the hub even when they share the same
    // hub.topic.  A shared topic with applicationId=WFM for both would cause
    // many hubs to deduplicate the subscriptions and route all events only to
    // the last-registered WebSocket, breaking the other integration.
    const urlParams = this.dataService.transformUrlParams({
      'hub.topic': topic,
      'hub.channel.type': 'websocket',
      'hub.mode': 'subscribe',
      'hub.events': subscriptionEvents,
      applicationId: appId || appConstants.APP_ID.WFM
    });

    return { subscribeUrl, urlParams };
  }
}
